import React, { useState, useEffect } from "react";
import api, { API_BASE } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Upload, Download, CheckCircle2, AlertTriangle, XCircle, Copy,
  RotateCcw, ChevronDown, ChevronRight, FileText, Loader2,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Historical Direct-Clients Import (2-step: preview → confirm)      */
/* ------------------------------------------------------------------ */
export default function HistoricalImportPanel() {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [batch, setBatch] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [filter, setFilter] = useState("all"); // all | valid | invalid | duplicate | warnings
  const [expanded, setExpanded] = useState({});
  const [batches, setBatches] = useState([]);

  const loadBatches = async () => {
    try {
      const { data } = await api.get("/api/imports/historical/batches");
      setBatches(data || []);
    } catch (e) {
      // Panel may render before auth is ready; log so the cause is discoverable.
      if (e.response?.status !== 401) logger.error("loadBatches failed", e);
    }
  };
  useEffect(() => { loadBatches(); }, []);

  const runPreview = async () => {
    if (!file) return toast.error("Choose a CSV first");
    setPreviewing(true); setBatch(null); setConfirmChecked(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/imports/historical/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      toast.success(`Preview ready — ${data.summary.valid} valid, ${data.summary.invalid} invalid`);
      loadBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const runConfirm = async () => {
    if (!batch || !confirmChecked) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/api/imports/historical/confirm", {
        batch_id: batch.id, confirm: true,
      });
      toast.success(`Imported — projects: ${data.inserted.projects}, invoices: ${data.inserted.invoices}, payments: ${data.inserted.payment_transactions}`);
      // Reload the batch to reflect applied status
      const { data: refreshed } = await api.get(`/api/imports/historical/batches/${batch.id}`);
      setBatch(refreshed);
      loadBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    } finally { setConfirming(false); }
  };

  const runRollback = async (batchId) => {
    if (!window.confirm("Rollback this batch? Only records inserted by this batch will be removed."))
      return;
    try {
      const { data } = await api.post(`/api/imports/historical/batches/${batchId}/rollback`);
      toast.success(`Rolled back — projects: ${data.deleted.projects}, invoices: ${data.deleted.invoices}`);
      loadBatches();
      if (batch?.id === batchId) {
        const { data: refreshed } = await api.get(`/api/imports/historical/batches/${batchId}`);
        setBatch(refreshed);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  const downloadReport = (batchId) => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE}/imports/historical/batches/${batchId}/report.csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `import_report_${batchId}.csv`; a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Failed to download report"));
  };

  const openBatch = async (batchId) => {
    try {
      const { data } = await api.get(`/api/imports/historical/batches/${batchId}`);
      setBatch(data); setConfirmChecked(false);
      setTimeout(() => document.getElementById("historical-preview-block")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error("Failed to load batch"); }
  };

  const rows = batch?.row_reports || [];
  const filteredRows = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "warnings") return (r.warnings?.length || 0) > 0 || r.stages?.some((s) => s.warnings?.length);
    return r.status === filter;
  });

  const s = batch?.summary;

  return (
    <div className="space-y-6" data-testid="historical-import-panel">
      {/* -------- Upload block -------- */}
      <Card className="border-slate-200 shadow-none p-6 space-y-4">
        <div>
          <div className="font-heading font-medium text-lg">Historical Direct-Clients CSV Import</div>
          <p className="text-sm text-slate-500 mt-1">
            Upload the wide "Direct Clients" spreadsheet. This is a <b>two-step</b> flow: preview first,
            then explicitly confirm to apply. Projects, invoices, payments &amp; clients are inserted
            atomically per batch and can be <b>rolled back</b> from below.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file" accept=".csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setBatch(null); }}
            className="text-sm"
            data-testid="historical-file-input"
          />
          <Button
            onClick={runPreview}
            disabled={previewing || !file}
            className="bg-brand hover:bg-brand-hover"
            data-testid="historical-preview-btn"
          >
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {previewing ? "Parsing…" : "Preview import"}
          </Button>
        </div>
      </Card>

      {/* -------- Preview / apply block -------- */}
      {batch && (
        <Card id="historical-preview-block" className="border-slate-200 shadow-none p-6 space-y-4">
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <div className="font-heading font-medium text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Preview · {batch.filename}
                <BatchStatusPill status={batch.status} />
              </div>
              <div className="text-xs text-slate-500 mt-1 font-mono">
                Batch: {batch.id} · {new Date(batch.created_at).toLocaleString()}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadReport(batch.id)}
                      data-testid="historical-download-report">
                <Download className="w-4 h-4 mr-2" />Download report
              </Button>
              {batch.status === "applied" && (
                <Button variant="outline" size="sm" onClick={() => runRollback(batch.id)}
                        data-testid="historical-rollback-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />Rollback batch
                </Button>
              )}
            </div>
          </div>

          {/* Summary cards */}
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryTile label="Total rows" value={s.total} />
              <SummaryTile label="Valid" value={s.valid} tone="emerald" />
              <SummaryTile label="Invalid" value={s.invalid} tone="rose" />
              <SummaryTile label="Duplicate" value={s.duplicate} tone="amber" />
              <SummaryTile label="Invoices · LLC" value={s.invoices_llc} />
              <SummaryTile label="Invoices · India" value={s.invoices_india} />
              <SummaryTile label="New clients (auto)" value={s.auto_create_clients} />
              <SummaryTile label="Warnings" value={s.warnings} tone="amber" />
              <SummaryTile label="Unknown entity" value={s.invoices_unknown_entity} tone="amber" />
            </div>
          )}

          {/* Filter */}
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Filter</span>
            {["all", "valid", "invalid", "duplicate", "warnings"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition
                  ${filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`historical-filter-${f}`}
              >
                {f}
              </button>
            ))}
            <span className="text-xs text-slate-400 ml-auto">
              Showing {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows
            </span>
          </div>

          {/* Rows table (virtualized-lite: cap render at 300 to keep DOM fast) */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="max-h-[560px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 w-16">Row</th>
                    <th className="px-2 py-2 w-20">Status</th>
                    <th className="px-2 py-2">Client</th>
                    <th className="px-2 py-2">Project</th>
                    <th className="px-2 py-2 w-40">Project Code</th>
                    <th className="px-2 py-2 w-16">FY</th>
                    <th className="px-2 py-2 w-16">Cur</th>
                    <th className="px-2 py-2 w-24 text-right">Budget</th>
                    <th className="px-2 py-2 w-12 text-center">Stg</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.slice(0, 300).map((r) => {
                    const key = `${r.row_num}`;
                    const isOpen = expanded[key];
                    return (
                      <React.Fragment key={key}>
                        <tr
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer
                            ${r.status === "invalid" ? "bg-rose-50/40" : ""}
                            ${r.status === "duplicate" ? "bg-amber-50/40" : ""}`}
                          onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                        >
                          <td className="px-2 py-1.5 text-slate-400">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 tabular-nums">{r.row_num}</td>
                          <td className="px-2 py-1.5"><StatusChip s={r.status} /></td>
                          <td className="px-2 py-1.5">
                            <span className="truncate max-w-[220px] inline-block align-middle">{r.client}</span>
                            {r.auto_create_client && (
                              <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1.5 border-emerald-300 text-emerald-700">new</Badge>
                            )}
                          </td>
                          <td className="px-2 py-1.5 truncate max-w-[220px]">{r.project_name}</td>
                          <td className="px-2 py-1.5 font-mono text-xs text-slate-600">
                            {r.project_code_final || <span className="text-slate-400 italic">auto</span>}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500">{r.fy || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-500">{r.currency}</td>
                          <td className="px-2 py-1.5 tabular-nums text-right">
                            {r.project_budget?.toLocaleString(undefined, { style: "currency", currency: r.currency || "USD" }) || "—"}
                          </td>
                          <td className="px-2 py-1.5 text-center text-slate-500">{r.stages?.length || 0}</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t border-slate-100 bg-slate-50/60">
                            <td colSpan={10} className="px-4 py-3">
                              <RowDetail r={r} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {filteredRows.length > 300 && (
                <div className="p-2 text-center text-xs text-slate-400 border-t border-slate-100 bg-white">
                  … {filteredRows.length - 300} more rows hidden — download the CSV report to see them all
                </div>
              )}
            </div>
          </div>

          {/* Confirm gate */}
          {batch.status === "preview" && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox
                  checked={confirmChecked}
                  onCheckedChange={(v) => setConfirmChecked(!!v)}
                  data-testid="historical-confirm-checkbox"
                  className="mt-0.5"
                />
                <span>
                  I've reviewed the preview above. I understand that <b>{s?.valid || 0}</b> rows
                  will be imported (creating <b>{s?.auto_create_clients || 0}</b> new clients),
                  invalid/duplicate rows will be skipped, and this batch can be rolled back later.
                </span>
              </label>
              <div className="flex gap-3">
                <Button
                  onClick={runConfirm}
                  disabled={!confirmChecked || confirming || (s?.valid || 0) === 0}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="historical-confirm-btn"
                >
                  {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {confirming ? "Applying…" : `Confirm & Import ${s?.valid || 0} rows`}
                </Button>
              </div>
            </div>
          )}
          {batch.status === "applied" && (
            <div className="border-t border-slate-100 pt-4 flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              Applied on {batch.applied_at ? new Date(batch.applied_at).toLocaleString() : "—"}
            </div>
          )}
          {batch.status === "rolled_back" && (
            <div className="border-t border-slate-100 pt-4 flex items-center gap-2 text-sm text-slate-600">
              <RotateCcw className="w-4 h-4" />
              Rolled back on {batch.rolled_back_at ? new Date(batch.rolled_back_at).toLocaleString() : "—"}
            </div>
          )}
        </Card>
      )}

      {/* -------- Past batches -------- */}
      <Card className="border-slate-200 shadow-none p-6">
        <div className="flex justify-between items-center mb-3">
          <div className="font-heading font-medium">Past import batches</div>
          <Button variant="outline" size="sm" onClick={loadBatches} data-testid="historical-refresh-batches">Refresh</Button>
        </div>
        {batches.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">No batches yet.</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">File</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Valid / Total</th>
                  <th className="px-3 py-2 text-right">Projects</th>
                  <th className="px-3 py-2 text-right">Invoices</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{b.filename}</td>
                    <td className="px-3 py-2"><BatchStatusPill status={b.status} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {b.summary?.valid || 0} / {b.summary?.total || 0}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {b.status === "applied" ? (b.inserted?.projects?.length || 0) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {b.status === "applied" ? (b.inserted?.invoices?.length || 0) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openBatch(b.id)}>Open</Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadReport(b.id)}>Report</Button>
                      {b.status === "applied" && (
                        <Button variant="ghost" size="sm" onClick={() => runRollback(b.id)}
                                className="text-rose-700 hover:text-rose-800">Rollback</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------- small subcomponents ---------- */

function SummaryTile({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone]}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-semibold tabular-nums mt-0.5">{value?.toLocaleString?.() || 0}</div>
    </div>
  );
}

function StatusChip({ s }) {
  if (s === "valid") return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3" />valid</span>;
  if (s === "invalid") return <span className="inline-flex items-center gap-1 text-rose-700 text-xs"><XCircle className="w-3 h-3" />invalid</span>;
  if (s === "duplicate") return <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><Copy className="w-3 h-3" />dupe</span>;
  return <span className="text-slate-500 text-xs">{s}</span>;
}

function BatchStatusPill({ status }) {
  const map = {
    preview: ["bg-slate-100 text-slate-700", "preview"],
    applied: ["bg-emerald-100 text-emerald-800", "applied"],
    rolled_back: ["bg-slate-200 text-slate-600", "rolled back"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-700", status || "?"];
  return <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${cls}`}>{label}</span>;
}

function RowDetail({ r }) {
  return (
    <div className="text-xs space-y-2">
      {r.errors?.length > 0 && (
        <div>
          <span className="font-medium text-rose-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Errors</span>
          <ul className="list-disc pl-5 mt-1 text-rose-700 space-y-0.5">
            {r.errors.map((e, i) => <li key={`${e}-${i}`}>{e}</li>)}
          </ul>
        </div>
      )}
      {r.warnings?.length > 0 && (
        <div>
          <span className="font-medium text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Warnings</span>
          <ul className="list-disc pl-5 mt-1 text-amber-700 space-y-0.5">
            {r.warnings.map((w, i) => <li key={`${w}-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}
      <div>
        <span className="font-medium text-slate-700">Stages / invoices</span>
        <table className="w-full mt-1 text-[11px]">
          <thead className="text-slate-500 text-left">
            <tr>
              <th className="pr-2">#</th>
              <th className="pr-2">Amount</th>
              <th className="pr-2">Invoice</th>
              <th className="pr-2">Entity</th>
              <th className="pr-2">Paid on</th>
              <th className="pr-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {r.stages?.map((s, i) => (
              <tr key={s.invoice_no ? `${s.invoice_no}-${s.stage_number}` : `stage-${i}`} className={`border-t border-slate-200/60 ${s.shared_invoice ? "text-sky-700" : ""}`}>
                <td className="pr-2 py-0.5">{s.stage_number}</td>
                <td className="pr-2 py-0.5 tabular-nums">{Number(s.amount).toLocaleString(undefined, { style: "currency", currency: r.currency || "USD" })}</td>
                <td className="pr-2 py-0.5 font-mono">{s.invoice_no || <span className="text-slate-400">—</span>}</td>
                <td className="pr-2 py-0.5">{s.entity ? <Badge variant="outline" className="text-[10px] py-0 px-1">{s.entity}</Badge> : <span className="text-slate-400">?</span>}</td>
                <td className="pr-2 py-0.5">{s.paid_on || <span className="text-slate-400">—</span>}</td>
                <td className="pr-2 py-0.5 text-slate-500">
                  {s.shared_invoice && <Badge variant="outline" className="text-[10px] py-0 px-1 mr-2 border-sky-300 text-sky-700">shared</Badge>}
                  {s.warnings?.join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
